import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { LOGIN_MUTATION } from '../graphql/mutations/auth.mutations';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loginSuccess, loginFailure, setLoading, clearError } from '../store/slices/authSlice';
import { LoginVariables, LoginResponse } from '../types/auth.types';
import { decodePermissions, hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../config/permissions';
import './Login.css';

const ADMIN_ONLY_MESSAGE =
  'Esta aplicación es solo para administradores de chatbot. Si crees que es un error, contacta al administrador.';

interface LoginMutationResponse {
  login: LoginResponse;
}

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, loading, error } = useAppSelector((state) => state.auth);

  const [loginMutation] = useMutation<LoginMutationResponse, LoginVariables>(LOGIN_MUTATION);

  useEffect(() => {
    // Si ya está autenticado, redirigir al dashboard
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const message = localStorage.getItem('sessionMessage');
    if (message) {
      dispatch(loginFailure(message));
      localStorage.removeItem('sessionMessage');
    }
  }, [dispatch]);

  useEffect(() => {
    // Limpiar errores cuando el componente se desmonta
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      dispatch(loginFailure('Por favor ingresa usuario y contraseña'));
      return;
    }

    dispatch(setLoading(true));
    dispatch(clearError());

    try {
      const variables: LoginVariables = {
        username,
        password,
        platform: 'web',
        instance: 'ORANGE',
      };

      const { data } = await loginMutation({ variables });

      if (data?.login?.success && data?.login?.data) {
        const raw = data.login.data;
        const permissions = await decodePermissions(raw.permissions);

        if (!hasPermission(permissions, PERMISSIONS.CHATBOT_ADMIN)) {
          dispatch(loginFailure(ADMIN_ONLY_MESSAGE));
          return;
        }

        dispatch(
          loginSuccess({
            tenantId: raw.tenantId,
            userId: raw.userId,
            userName: raw.userName,
            name: raw.name,
            tenantType: raw.tenantType,
            token: raw.token,
            permissions,
          })
        );
        navigate('/dashboard');
      } else {
        const errorMessage = data?.login?.message || 'Error al iniciar sesión';
        const errors = data?.login?.errors || [];
        const fullErrorMessage = errors.length > 0
          ? `${errorMessage}: ${errors.join(', ')}`
          : errorMessage;

        dispatch(loginFailure(fullErrorMessage));
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.message || 'Error de conexión con el servidor';
      dispatch(loginFailure(errorMessage));
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>iPlug Chatbot</h1>
          <p>Administración</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Usuario</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Ingresa tu usuario"
              disabled={loading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa tu contraseña"
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
